import React, { useCallback, useEffect, useRef, useState } from "react"
import { DateTime, Interval } from "luxon"
import Sticky from "react-sticky-fill"
import Repeatable from "react-repeatable"
import EventsList from "./EventsList"
import { ReactComponent as Arrow } from "./images/arrow_schedule.svg"

function* daysMap(interval) {
  let i = 0
  let cursor = interval.start.startOf("day")

  while (cursor < interval.end) {
    yield [i, cursor]
    cursor = cursor.plus({ days: 1 })
    i++
  }
}

const DailyView = (props) => {
  const {
    events,
    filters,
    ordering,
    path,
    screenings,
    ticketing,
    title,
    trackList,
  } = props

  const [days, setDays] = useState([])
  const [daysCount, setDaysCount] = useState(0)
  const [cellSize, setCellSize] = useState(window.innerWidth < 960 ? 194 : 270)
  const [hideArrows, setHideArrows] = useState({
    prev: false,
    next: false,
  })

  let grid = document.querySelector(".schedule__grid")

  const changeCellSize = useCallback(() => {
    if (window.innerWidth < 960) {
      setCellSize(194)
    } else {
      setCellSize(270)
    }
  })

  const toggleArrows = useCallback(() => {
    grid = document.querySelector(".schedule__grid")
    const firstRow = grid.querySelector(".schedule__row")

    if (firstRow) {
      const editedArrow = { ...hideArrows }

      if (grid.scrollLeft > 0) {
        editedArrow.prev = false
      } else {
        editedArrow.prev = true
      }

      if (grid.scrollLeft === grid.scrollWidth - window.innerWidth) {
        editedArrow.next = true
      } else {
        editedArrow.next = false
      }

      setHideArrows(editedArrow)
    }
  })

  useEffect(() => {
    grid = document.querySelector(".schedule__grid")

    window.addEventListener("load", changeCellSize)
    window.addEventListener("resize", changeCellSize)
    grid.addEventListener("scroll", toggleArrows)

    return () => {
      window.onload = changeCellSize
      window.removeEventListener("resize", changeCellSize)
      grid.removeEventListener("scroll", toggleArrows)
    }
  }, [])

  useEffect(() => {
    if (events.length) {
      const editedDays = [...days]
      let minDate = DateTime.fromISO(events[0].start)

      events.forEach((event) => {
        event.start = DateTime.fromISO(event.start).setZone("America/New_York")
        event.end = DateTime.fromISO(event.end).setZone("America/New_York")

        if (event.start < minDate) {
          minDate = event.start
        }

        const startString = `${event.start.get("monthShort")} ${event.start.get(
          "day",
        )}`
        const endString = `${event.end.get("monthShort")} ${event.end.get(
          "day",
        )}`
        if (startString !== endString) {
          let interval = Interval.fromDateTimes(event.start, event.end)
          const daysCount = interval.count("days")
          for (const [index, day] of daysMap(interval)) {
            let startDay = `${day.get("monthShort")} ${day.get("day")}`
            const foundDay = editedDays.find((d) => d.day === startDay)
            const repeatingEvent = { ...event }
            if (index === 0) {
              repeatingEvent.end = day.endOf("day")
            } else if (index === daysCount - 1) {
              repeatingEvent.start = day
            } else {
              repeatingEvent.start = day
              repeatingEvent.end = day.endOf("day")
            }
            if (foundDay) {
              foundDay.events.push(repeatingEvent)
            } else {
              editedDays.push({
                day: startDay,
                date: repeatingEvent.start,
                events: [repeatingEvent],
              })
            }
          }
        } else {
          const startDay = `${event.start.get("monthShort")} ${event.start.get(
            "day",
          )}`
          const foundDay = editedDays.find((d) => d.day === startDay)
          if (foundDay) {
            foundDay.events.push(event)
          } else {
            editedDays.push({
              day: startDay,
              date: event.start,
              events: [event],
            })
          }
        }
      })

      setDays(editedDays.sort((a, b) => a.date - b.date))
      setDaysCount(editedDays.length)
      toggleArrows()
    }
  }, [events])

  useEffect(() => {
    if (days.length) {
      const today = DateTime.local().toFormat("LLL d")
      const current = days.find((day) => day.day === today)

      if (current) {
        const daysOver = days.findIndex((day) => day.day === today)
        grid.scrollLeft = daysOver * cellSize
      }
    }
  }, [days, trackList])

  useEffect(() => {
    if (filters.date && days.length) {
      const newDate = DateTime.fromISO(filters.date).toFormat("LLL d")
      const current = days.find((day) => day.day === newDate)

      if (current) {
        const daysOver = days.findIndex((day) => day.day === newDate)
        grid.scrollLeft = daysOver * cellSize
      }
    }
  }, [filters.date])

  const scrollHoursLeft = () => {
    if (grid.scrollLeft > 0) {
      grid.scrollBy({
        top: 0,
        left: -window.innerWidth,
        behavior: "smooth",
      })
    }
  }

  const scrollHoursRight = () => {
    grid.scrollBy({
      top: 0,
      left: window.innerWidth,
      behavior: "smooth",
    })
  }

  return (
    <div className="schedule__wrapper">
      <div className="schedule">
        <div className="schedule__grid">
          <Repeatable
            className={`prev ${hideArrows.prev ? "hide" : ""}`}
            aria-label="previous"
            tag="button"
            repeatDelay={500}
            repeatInterval={500}
            onTouchStart={(event) => event.preventDefault}
            onClick={scrollHoursLeft}
            onHold={scrollHoursLeft}
          >
            <Arrow />
          </Repeatable>

          <Repeatable
            className={`next ${hideArrows.next ? "hide" : ""}`}
            aria-label="next"
            tag="button"
            repeatDelay={500}
            repeatInterval={500}
            onTouchStart={(event) => event.preventDefault}
            onClick={scrollHoursRight}
            onHold={scrollHoursRight}
          >
            <Arrow />
          </Repeatable>

          <div className="schedule__hours">
            <div className="schedule__hours__inner">
              {days.map((day, index) => {
                return (
                  <h3 key={index} className="has-events">
                    {day.date.toFormat("cccc, LLL. d")}
                  </h3>
                )
              })}
            </div>
          </div>

          {days.find((d) => d.events.find((e) => !e.tracks.length)) &&
            filters.track === "all" && (
              <div
                className="schedule__row"
                style={{
                  minWidth: cellSize * daysCount,
                  maxWidth: cellSize * daysCount,
                }}
              >
                <Sticky className="track">
                  <div className="track__inner">
                    <h3>{title}</h3>
                  </div>
                </Sticky>

                <div className="schedule__borders">
                  {[...Array(daysCount)].map((_, index) => (
                    <span key={index} />
                  ))}
                </div>

                <EventsList
                  rows={daysCount}
                  events={days}
                  cellSize={cellSize}
                  track="unassigned"
                  filters={filters}
                  screenings={screenings}
                  ticketing={ticketing}
                  path={path}
                />
              </div>
            )}
          {trackList
            .sort((a, b) => {
              const aOrder = ordering.find((o) => o.name === a)
              const bOrder = ordering.find((o) => o.name === b)

              if (aOrder && bOrder) {
                return aOrder.position > bOrder.position ? 1 : -1
              }

              return 2
            })
            .map((track) => (
              <React.Fragment key={track}>
                {days.find((d) =>
                  d.events.find((e) => e.tracks.includes(track)),
                ) &&
                  track !== "unassigned" &&
                  (filters.track === "all" || filters.track === track) && (
                    <div
                      className="schedule__row"
                      style={{
                        minWidth: cellSize * daysCount,
                        maxWidth: cellSize * daysCount,
                      }}
                    >
                      <Sticky className="track">
                        <div className="track__inner">
                          <h3>{track}</h3>
                        </div>
                      </Sticky>

                      <div className="schedule__borders">
                        {[...Array(daysCount)].map((_, index) => (
                          <span key={index} />
                        ))}
                      </div>

                      <EventsList
                        rows={daysCount}
                        events={days}
                        cellSize={cellSize}
                        track={track}
                        filters={filters}
                        screenings={screenings}
                        ticketing={ticketing}
                        path={path}
                      />
                    </div>
                  )}
              </React.Fragment>
            ))}
        </div>
      </div>
    </div>
  )
}

export default DailyView
